// Runtime hardening for the Image Edit web surface.
//
// Kept at the HTTP boundary on purpose: ImageEditWorkspace has accumulated a lot
// of UI state, while these are transport invariants shared by every future
// Image Edit surface. The interceptor never changes the source silently:
//   - filename match only proposes a bind; backend proves the asset↔shot link;
//   - deep generated→generated chains require an explicit user choice;
//   - QC REVIEW/RETRY requires an explicit human override;
//   - live propagation is blocked until the backend worker is durable/resumable.
import axios from "axios";
import { propagationIsBlocked } from "./imageEditScope";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const INSTALLED_KEY = "__orcaImageEditRuntimeInstalled";

function cleanUrl(url) {
  return String(url || "").split("?")[0].replace(/\/$/, "");
}

function parseData(data) {
  if (data && typeof data === "object") return data;
  if (typeof data !== "string") return {};
  try { return JSON.parse(data); } catch (_e) { return {}; }
}

function isMethod(config, method) {
  return String(config?.method || "get").toLowerCase() === method;
}

function imageEditJobIdFrom(url, suffix) {
  const path = cleanUrl(url);
  const marker = "/image-edit-jobs/";
  const at = path.lastIndexOf(marker);
  if (at < 0 || !path.endsWith(suffix)) return null;
  const between = path.slice(at + marker.length, path.length - suffix.length);
  return between && !between.includes("/") ? between : null;
}

function isCreateJob(config) {
  if (!isMethod(config, "post")) return false;
  const path = cleanUrl(config?.url);
  return path.endsWith("/api/image-edit-jobs") || path.endsWith("/image-edit-jobs");
}

function isAnchorPropagate(config) {
  if (!isMethod(config, "post")) return false;
  return cleanUrl(config?.url).endsWith("/image-edit-jobs/anchor-propagate");
}

function latestQc(job) {
  const versions = Array.isArray(job?.versions) ? job.versions : [];
  for (let i = versions.length - 1; i >= 0; i -= 1) {
    if (versions[i]?.qc) return versions[i].qc;
  }
  return null;
}

function precisionDepth(job) {
  return (job?.versions || []).filter((v) => v?.kind === "precision_edit").length;
}

async function loadJob(jobId) {
  const r = await axios.get(`${API}/image-edit-jobs/${jobId}`);
  return r.data || {};
}

function canAsk() {
  return typeof window !== "undefined" && typeof window.confirm === "function";
}

export function installImageEditRuntime() {
  if (typeof globalThis !== "undefined" && globalThis[INSTALLED_KEY]) return;
  if (typeof globalThis !== "undefined") globalThis[INSTALLED_KEY] = true;

  axios.interceptors.request.use(async (config) => {
    // A dry-run is deterministic and persisted. Live propagation is NOT yet a
    // production contract: process_batch lives in asyncio.create_task, has no
    // restart resume/lease, and an anchor's raster protection mask is not part
    // of last_precision_recipe. A button that can silently lose both guarantees
    // is worse than an explicit temporary block.
    if (isAnchorPropagate(config)) {
      const body = parseData(config.data);
      if (propagationIsBlocked(body)) {
        throw new axios.Cancel(
          "Live Image Edit propagation is temporarily blocked: the batch worker is not restart-resumable and pixel-lock masks are not yet portable across shots. Use Plan only."
        );
      }
      return config;
    }

    const precisionJobId = imageEditJobIdFrom(config?.url, "/precision-edit");
    if (isMethod(config, "post") && precisionJobId
        && !config?.headers?.["X-Orca-Image-Lineage-Checked"]) {
      try {
        const current = await loadJob(precisionJobId);
        const depth = precisionDepth(current);
        // The first two passes remain available as a deliberate refine chain.
        // Before pass 3, make the generational lineage a conscious decision.
        if (depth >= 2 && canAsk()) {
          const rebase = window.confirm(
            `This edit would use a generated image as source again (generation depth ${depth} → ${depth + 1}).\n\nOK: REBASE from the immutable original plate (recommended).\nCancel: continue refining the current generated result.`
          );
          if (rebase) {
            const rb = await axios.post(`${API}/image-edit-jobs/${precisionJobId}/rebase`);
            const nextId = rb.data?.id;
            if (!nextId) throw new Error("Rebase did not return a new Image Edit job");
            // The SERVER owns rebase semantics, including remapping Artist Mode
            // placement reference ids to the freshly copied references. Doing
            // that in the browser would submit stale old-job ids to /visual-plan.
            // The precision request itself was authored BEFORE this interceptor
            // chose to rebase, so replace its reference paths with the copied
            // refs too. Otherwise the first rebased recipe would audit paths from
            // the old job even though the bytes happen to be identical.
            const rebasedRefs = Array.isArray(rb.data?.references) ? rb.data.references : [];
            if (rebasedRefs.length) {
              const requestBody = parseData(config.data);
              config.data = {
                ...requestBody,
                reference_storage_paths: rebasedRefs.map((r) => r?.storage_path).filter(Boolean),
              };
            }
            config.url = cleanUrl(config.url).replace(
              `/image-edit-jobs/${precisionJobId}/precision-edit`,
              `/image-edit-jobs/${nextId}/precision-edit`,
            );
          }
        }
      } catch (ex) {
        // A failed lineage preflight must not invent a block on an otherwise
        // valid edit. Backend/normal request still decides the actual operation.
        if (axios.isCancel?.(ex)) throw ex;
        console.warn("[image-edit] lineage preflight unavailable", ex);
      }
      config.headers = { ...(config.headers || {}), "X-Orca-Image-Lineage-Checked": "1" };
      return config;
    }

    const approveJobId = imageEditJobIdFrom(config?.url, "/approve");
    if (isMethod(config, "post") && approveJobId) {
      let override = false;
      try {
        const current = await loadJob(approveJobId);
        const qc = latestQc(current);
        if (qc?.state && qc.state !== "AUTO_PASS") {
          if (!canAsk()) {
            throw new axios.Cancel(
              `Approval requires explicit QC override because the latest result is ${qc.state}.`
            );
          }
          override = window.confirm(
            `Latest Image Edit QC is ${qc.state}${qc.reason ? ` (${qc.reason})` : ""}.\n\nApprove anyway as a HUMAN OVERRIDE?`
          );
          if (!override) {
            throw new axios.Cancel("Approval cancelled: QC override was not confirmed.");
          }
        }
      } catch (ex) {
        if (axios.isCancel?.(ex)) throw ex;
        console.warn("[image-edit] approval QC preflight unavailable", ex);
      }
      config.url = cleanUrl(config.url).replace(
        `/image-edit-jobs/${approveJobId}/approve`,
        `/image-edit-jobs/${approveJobId}/approve-safe`,
      );
      config.headers = {
        ...(config.headers || {}),
        ...(override ? { "X-Orca-QC-Override": "true" } : {}),
      };
      return config;
    }

    return config;
  });

  axios.interceptors.response.use(async (response) => {
    if (!isCreateJob(response?.config)) return response;
    const sent = parseData(response.config.data);
    const shotHint = sent._orca_shot_scope_hint;
    const jobId = response.data?.id;
    if (!shotHint || !jobId || response.data?.shot_id) return response;
    try {
      const bound = await axios.post(
        `${API}/image-edit-jobs/${jobId}/bind-shot`,
        { shot_id: shotHint },
      );
      response.data = bound.data;
    } catch (ex) {
      // Editing the exact selected clip remains valid. Binding is deliberately
      // fail-closed: Approval stays disabled rather than guessing the shot.
      response.data = {
        ...(response.data || {}),
        _orca_binding_warning: ex?.response?.data?.detail || ex?.message || "Shot binding was not authoritative",
      };
      console.warn("[image-edit] shot scope not bound", response.data._orca_binding_warning);
    }
    return response;
  });
}

installImageEditRuntime();

export const _test = {
  cleanUrl, parseData, imageEditJobIdFrom, isCreateJob, isAnchorPropagate,
  latestQc, precisionDepth, propagationIsBlocked,
};
